// content/AIPosts/AIPostsViewPage.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Grid, Column, Button, Breadcrumb, BreadcrumbItem } from '@carbon/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import amplitude from './../../analytics/amplitude';
import { resolveKey, fetchContentByKey } from './api';

const ShareBar = ({ url, title }) => {
  const enc = encodeURIComponent;
  const links = [
    { name:'X/Twitter', href:`https://twitter.com/intent/tweet?url=${enc(url)}&text=${enc(title)}` },
    { name:'LinkedIn', href:`https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}` },
    { name:'Facebook', href:`https://www.facebook.com/sharer/sharer.php?u=${enc(url)}` },
  ];
  return (
    <div style={{display:'flex', gap:12, alignItems:'center', margin:'12px 0'}}>
      <span style={{opacity:0.7}}>Share:</span>
      {links.map(l => (
        <Button key={l.name} kind="ghost" size="sm" onClick={()=>{
          window.open(l.href,'_blank','noopener');
          try { amplitude.track('Share Post', { network:l.name, url }); } catch {}
        }}>{l.name}</Button>
      ))}
      <Button kind="ghost" size="sm" onClick={() => {
        navigator.clipboard.writeText(url);
        try { amplitude.track('Copy Post Link', { url }); } catch {}
      }}>Copy link</Button>
    </div>
  );
};

const BreadcrumbsBar = ({ topic, date, slug, onBack }) => {
  const topicLabel = (topic || '').replace(/-/g, ' ');
  const slugLabel  = (slug  || '').replace(/-/g, ' ');
  return (
    <nav aria-label="Breadcrumb" style={{ margin: '8px 0 12px' }}>
      <Breadcrumb noTrailingSlash>
        <BreadcrumbItem>
          <a href="/#/posts">AI Posts</a>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <a href={`/#/posts/${topic}`}>{topicLabel || 'topic'}</a>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>{slugLabel || 'post'}</BreadcrumbItem>
      </Breadcrumb>
      <div style={{ marginTop: 8 }}>
        <Button kind="ghost" size="sm" onClick={onBack}>
          ← Back to {topicLabel || 'topic'}
        </Button>
      </div>
    </nav>
  );
};

const AIPostsViewPage = () => {
  const { topic, date, slug } = useParams();
  const nav = useNavigate();
  const [md, setMd] = useState('');
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { key } = await resolveKey(topic, date, slug);
        const { meta, body_only } = await fetchContentByKey(key);
        setMd(body_only || '');
        setMeta(meta);
        document.title = meta?.title ? `${meta.title} — Fresh Focus AI` : `Post — Fresh Focus AI`;
        try { amplitude.track('View Post', { topic, date, slug, model: meta?.model }); } catch {}
      } catch {
        setMd('# Not found\nSorry, this post could not be located.');
      }
    })();
  }, [topic, date, slug]);

  useEffect(() => {
    return () => { document.title = 'Fresh Focus AI'; };
  }, []);
  

  const canonical = `${window.location.origin}/#/posts/${topic}/${date}/${slug}`;
  const query = new URLSearchParams((window.location.hash.split('?')[1]) || window.location.search);
  const fullBleed = meta?.layout === 'full' || query.get('wide') === '1';

  const handleBack = () => nav(`/posts/${topic}`);

  return (
    <Grid fullWidth>
      <Column lg={16} md={8} sm={4}>
        {/* Top breadcrumbs */}
        <BreadcrumbsBar topic={topic} date={date} slug={slug} onBack={handleBack} />

        {meta && (
          <>
            <h3 style={{marginBottom:4}}>{meta.title || slug.replace(/-/g,' ')}</h3>
            <div style={{opacity:0.7, marginBottom:6, fontSize:12}}>
              {meta.topic} • {meta.date} • {meta.model}
            </div>
            {meta.summary && (
              <div style={{
                fontSize:14, opacity:0.85, marginBottom:12,
                borderLeft:'3px solid #e0e0e0', paddingLeft:8
              }}>
                {meta.summary}
              </div>
            )}
            {meta?.hero_url && (
              <img
                src={meta.hero_url}
                alt={meta.title || 'Hero image'}
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                  borderRadius: 12,
                  margin: '8px 0 14px 0',
                  display: 'block'
                }}
              />
            )}
            <ShareBar url={canonical} title={meta.title || slug} />
          </>
        )}

        {/* Markdown */}
        <div className="ai-post__wrap">
          <div className={`ai-post__markdown markdown-body llm-response-area ${fullBleed ? 'is-full' : ''}`}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={{
                a: ({node, ...props}) => (
                  <a {...props} target="_blank" rel="noopener noreferrer" />
                ),
                table: ({node, ...props}) => (
                  <div className="md-table-wrap">
                    <table {...props}>{props.children}</table>
                  </div>
                ),
                img: ({node, ...props}) => (
                  <img {...props} className="md-img" alt={props.alt || ''} />
                ),
                code: ({inline, className, children, ...props}) => {
                  if (inline) {
                    return <code className={`inline-code ${className || ''}`} {...props}>{children}</code>;
                  }
                  return (
                    <pre className="code-block-container">
                      <div className="code-block-header">
                        <span className="language-label">{(className || '').replace('language-','') || 'code'}</span>
                      </div>
                      <code className={`syntax-highlighter ${className || ''}`} {...props}>{children}</code>
                    </pre>
                  );
                },
              }}
            >
              {md}
            </ReactMarkdown>
          </div>
        </div>

        {/* Bottom breadcrumbs to mirror top */}
        <div style={{ marginTop: 16 }}>
          <BreadcrumbsBar topic={topic} date={date} slug={slug} onBack={handleBack} />
        </div>
      </Column>
    </Grid>
  );
};

export default AIPostsViewPage;
